import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, MessageSquare, User, Store, Loader2, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { playChatSound, areSoundsEnabled } from '../utils/notificationSounds';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace('https://', 'wss://').replace('http://', 'ws://');

const OrderChat = ({ orderId, orderStatus, darkMode = false }) => {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const isFuncionario = user?.role === 'station_admin';
  const isDark = darkMode || isFuncionario;

  // Get token for WebSocket auth
  const getToken = useCallback(() => (
    localStorage.getItem(isFuncionario ? 'halubri_funcionario_token' : 'halubri_token')
  ), [isFuncionario]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Load initial messages via REST API
  const loadMessages = useCallback(async () => {
    try {
      const res = await api().get(`/chat/${orderId}`);
      setMessages(res.data);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
    setLoading(false);
  }, [api, orderId]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const token = getToken();
    if (!token || !orderId) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${WS_URL}/api/ws/chat/${orderId}?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[Chat WS] Connected');
      setWsConnected(true);
      
      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          const newMsg = data.message;
          
          // Add message to list
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          
          // Play sound if message is from other user
          const isFromOther = newMsg.sender_id !== user?.id;
          if (isFromOther && areSoundsEnabled()) {
            playChatSound();
          }
        }
      } catch (err) {
        console.error('[Chat WS] Parse error:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('[Chat WS] Disconnected', event.code);
      setWsConnected(false);
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      // Reconnect after 5 seconds if not closed intentionally
      if (event.code !== 1000 && event.code !== 4001) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[Chat WS] Reconnecting...');
          connectWebSocket();
        }, 5000);
      }
    };

    ws.onerror = (err) => {
      console.error('[Chat WS] Error:', err);
    };

    wsRef.current = ws;
  }, [orderId, user?.id, getToken]);

  // Initial load and WebSocket connection
  useEffect(() => {
    loadMessages();
    connectWebSocket();

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [loadMessages, connectWebSocket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Send message via WebSocket or REST API fallback
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    if (orderStatus === 'completed' || orderStatus === 'cancelled') {
      toast.error('Não é possível enviar mensagens para pedidos finalizados');
      return;
    }

    setSending(true);

    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'message',
          message: newMessage.trim()
        }));
        setNewMessage('');
        setSending(false);
        return;
      } catch (err) {
        console.error('[Chat WS] Send error, falling back to REST');
      }
    }

    // Fallback to REST API
    try {
      const res = await api().post('/chat/send', { 
        order_id: orderId, 
        message: newMessage.trim() 
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (err) {
      toast.error('Erro ao enviar mensagem');
    }
    setSending(false);
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-AO', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for separators
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    }
    return date.toLocaleDateString('pt-AO', { day: 'numeric', month: 'short' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  // Check if order is closed
  const isOrderClosed = orderStatus === 'completed' || orderStatus === 'cancelled';

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${isDark ? 'bg-zinc-900' : 'bg-white'}`}>
        <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-sonangol-orange' : 'text-sonangol-orange'}`} />
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${isDark ? 'bg-zinc-900' : 'bg-white'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <MessageSquare className={`w-5 h-5 ${isDark ? 'text-sonangol-orange' : 'text-sonangol-orange'}`} />
          <span className={`font-barlow font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Chat do Pedido
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Connection status indicator */}
          <div className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-green-500' : 'text-yellow-500'}`}>
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'Conectado' : 'Reconectando...'}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadMessages}
            className={isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <div 
        className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}
        style={{ maxHeight: '300px', minHeight: '200px' }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className={`w-12 h-12 mb-3 ${isDark ? 'text-zinc-700' : 'text-gray-300'}`} />
            <p className={`font-medium ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
              Nenhuma mensagem ainda
            </p>
            <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
              {isFuncionario 
                ? 'Envie uma mensagem para o cliente' 
                : 'Envie uma mensagem para a estação'}
            </p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Separator */}
              <div className="flex items-center justify-center my-3">
                <span className={`px-3 py-1 text-xs rounded-full ${
                  isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {date}
                </span>
              </div>
              
              {/* Messages */}
              {dateMessages.map((msg) => {
                const isMyMessage = msg.sender_id === user?.id;
                const isStation = msg.sender_role === 'station_admin';
                
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} mb-2`}
                  >
                    <div className={`max-w-[80%] ${isMyMessage ? 'order-2' : 'order-1'}`}>
                      {/* Sender Info (only for other's messages) */}
                      {!isMyMessage && (
                        <div className="flex items-center gap-1 mb-1">
                          {isStation ? (
                            <Store className={`w-3 h-3 ${isDark ? 'text-sonangol-orange' : 'text-sonangol-orange'}`} />
                          ) : (
                            <User className={`w-3 h-3 ${isDark ? 'text-zinc-400' : 'text-gray-400'}`} />
                          )}
                          <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {isStation ? msg.sender_station_name || 'Estação' : msg.sender_name}
                          </span>
                        </div>
                      )}
                      
                      {/* Message Bubble */}
                      <div className={`px-4 py-2 rounded-2xl ${
                        isMyMessage 
                          ? 'bg-sonangol-orange text-white rounded-br-sm' 
                          : isDark 
                            ? 'bg-zinc-800 text-white rounded-bl-sm' 
                            : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                        <p className={`text-xs mt-1 ${
                          isMyMessage ? 'text-orange-200' : isDark ? 'text-zinc-500' : 'text-gray-400'
                        }`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className={`p-3 border-t ${isDark ? 'border-zinc-700 bg-zinc-900' : 'border-gray-200 bg-white'}`}>
        {isOrderClosed ? (
          <div className={`text-center py-2 text-sm ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
            Chat encerrado - Pedido {orderStatus === 'completed' ? 'concluído' : 'cancelado'}
          </div>
        ) : (
          <form onSubmit={sendMessage} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isFuncionario ? "Mensagem para o cliente..." : "Mensagem para a estação..."}
              className={`flex-1 ${
                isDark 
                  ? 'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500' 
                  : 'bg-gray-50 border-gray-200'
              }`}
              disabled={sending}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={sending || !newMessage.trim()}
              className="bg-sonangol-orange hover:bg-orange-600"
              data-testid="chat-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OrderChat;
